// src/screens/admin/NuestrosClientesAdminPanel.jsx
// Panel admin de "Nuestros Clientes": álbumes de fotos (con pixelado
// manual antes de subir), links de invitación de un solo uso para dejar
// reseña, moderación de reseñas, e interruptor de publicación + link de
// vista previa.
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import PixeladorFoto from '../../components/PixeladorFoto';
import { supabase } from '../../services/supabase';
import {
  adminListarAlbums, adminCrearAlbum, adminActualizarAlbum, adminEliminarAlbum,
  adminSubirFoto, adminSubirVideo, adminEliminarFoto, adminReemplazarFoto, adminListarResenas, adminModerarResena, adminEliminarResena,
  adminGetConfig, adminActualizarConfig, CATEGORIAS_RESENA,
  adminCrearInvitacionResena, adminListarInvitacionesResena, adminEliminarInvitacionResena,
  adminActualizarCaptionFoto, adminIntercambiarOrdenFotos, adminSetPortada, adminReordenarAlbums,
  adminAgregarTelefonosVerificados, adminListarTelefonosVerificados, adminEliminarTelefonoVerificado,
  normalizarTelefono, adminGetClientesEventos, isClientesTrackingOptedOut, setClientesTrackingOptOut,
} from '../../services/nuestrosClientesService';

function Estrellas({ n }) {
  return <span style={{ color: 'var(--gold, #FFB800)' }}>{'★'.repeat(n)}{'☆'.repeat(5 - n)}</span>;
}

// ── Álbumes ──────────────────────────────────────────────────────────
function AlbumesTab() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevoTitulo, setNuevoTitulo] = useState('');
  const [nuevoTituloPublico, setNuevoTituloPublico] = useState('');
  const [creando, setCreando] = useState(false);
  const [abierto, setAbierto] = useState(null); // album expandido
  // editor: null | { albumId, mode:'nueva', file } | { albumId, mode:'editar', fotoId, path, url }
  const [editor, setEditor] = useState(null);
  const [colaPendiente, setColaPendiente] = useState([]); // resto de archivos de una selección múltiple
  const [subiendo, setSubiendo] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [sesionEmail, setSesionEmail] = useState(null);

  useEffect(() => { supabase.auth.getUser().then(({ data }) => setSesionEmail(data?.user?.email || '(sin sesión)')); }, []);

  const cargar = () => {
    setErrorCarga(null);
    adminListarAlbums()
      .then(setAlbums)
      .catch((err) => { console.error('Error al listar álbumes:', err); setErrorCarga(err.message || err.details || err.hint || String(err)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { cargar(); }, []);

  const crear = async () => {
    if (!nuevoTitulo.trim()) return toast('Ponle un título interno (para identificarlo tú).', 'error');
    setCreando(true);
    try {
      const a = await adminCrearAlbum({ titulo: nuevoTitulo, tituloPublico: nuevoTituloPublico });
      setAlbums((prev) => [a, ...prev]);
      setNuevoTitulo(''); setNuevoTituloPublico('');
      toast('Álbum creado ✅');
    } catch { toast('Error al crear el álbum.', 'error'); }
    setCreando(false);
  };

  const toggleCampo = async (id, campo, valor) => {
    setAlbums((prev) => prev.map((a) => (a.id === id ? { ...a, [campo]: valor } : a)));
    try { await adminActualizarAlbum(id, { [campo]: valor }); } catch { toast('Error al actualizar.', 'error'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este álbum y todas sus fotos? No se puede deshacer.')) return;
    try {
      await adminEliminarAlbum(id);
      setAlbums((prev) => prev.filter((a) => a.id !== id));
      toast('Álbum eliminado.');
    } catch { toast('Error al eliminar.', 'error'); }
  };

  // Reasigna el orden de todos los álbumes según su posición tras mover
  // uno -- así funciona sin importar qué valor de "orden" tuvieran antes.
  const moverAlbum = async (index, direccion) => {
    const otroIndex = index + direccion;
    if (otroIndex < 0 || otroIndex >= albums.length) return;
    const nuevosAlbums = [...albums];
    [nuevosAlbums[index], nuevosAlbums[otroIndex]] = [nuevosAlbums[otroIndex], nuevosAlbums[index]];
    setAlbums(nuevosAlbums);
    try { await adminReordenarAlbums(nuevosAlbums.map((a, i) => ({ id: a.id, orden: i }))); }
    catch { toast('Error al reordenar.', 'error'); }
  };

  // Después de resolver una foto (subida, editada, o saltada), pasa a la
  // siguiente de la cola si venía de una selección múltiple.
  const siguienteEnCola = (albumId) => {
    setColaPendiente((cola) => {
      if (cola.length === 0) { setEditor(null); return cola; }
      const [next, ...resto] = cola;
      setEditor({ albumId, mode: 'nueva', file: next });
      return resto;
    });
  };

  const confirmarFoto = async (blob) => {
    const { albumId } = editor;
    setSubiendo(true);
    try {
      const album = albums.find((a) => a.id === albumId);
      const orden = (album?.fotos?.length || 0);
      const foto = await adminSubirFoto(albumId, blob, orden);
      setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, fotos: [...a.fotos, foto] } : a)));
      setAbierto((prev) => prev && prev.id === albumId && { ...prev, fotos: [...prev.fotos, foto] });
      toast('Foto agregada ✅');
      siguienteEnCola(albumId);
    } catch { toast('Error al subir la foto.', 'error'); }
    setSubiendo(false);
  };

  const confirmarEdicion = async (blob) => {
    const { albumId, fotoId, path } = editor;
    setSubiendo(true);
    try {
      const foto = await adminReemplazarFoto(albumId, fotoId, path, blob);
      setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, fotos: a.fotos.map((f) => (f.id === fotoId ? foto : f)) } : a)));
      setAbierto((prev) => prev && prev.id === albumId && { ...prev, fotos: prev.fotos.map((f) => (f.id === fotoId ? foto : f)) });
      toast('Foto actualizada ✅');
      setEditor(null);
    } catch { toast('Error al guardar los cambios.', 'error'); }
    setSubiendo(false);
  };

  // Los videos van directo, sin pasar por el editor de pixelado (no
  // aplica a video por ahora -- ver nota en nuestrosClientesService.js).
  const subirVideoDirecto = async (albumId, file) => {
    setSubiendo(true);
    try {
      const album = albums.find((a) => a.id === albumId);
      const orden = (album?.fotos?.length || 0);
      const video = await adminSubirVideo(albumId, file, orden);
      setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, fotos: [...a.fotos, video] } : a)));
      setAbierto((prev) => prev && prev.id === albumId && { ...prev, fotos: [...prev.fotos, video] });
      toast('Video agregado ✅');
    } catch { toast('Error al subir el video.', 'error'); }
    setSubiendo(false);
  };

  const cancelarEditor = () => {
    const albumId = editor?.albumId;
    if (colaPendiente.length > 0) siguienteEnCola(albumId);
    else setEditor(null);
  };

  const borrarFoto = async (albumId, fotoId, path) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      await adminEliminarFoto(fotoId, path);
      setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, fotos: a.fotos.filter((f) => f.id !== fotoId) } : a)));
      setAbierto((prev) => prev && { ...prev, fotos: prev.fotos.filter((f) => f.id !== fotoId) });
      toast('Foto eliminada.');
    } catch { toast('Error al eliminar la foto.', 'error'); }
  };

  const guardarCaption = async (albumId, fotoId, caption) => {
    setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, fotos: a.fotos.map((f) => (f.id === fotoId ? { ...f, caption } : f)) } : a)));
    setAbierto((prev) => prev && prev.id === albumId && { ...prev, fotos: prev.fotos.map((f) => (f.id === fotoId ? { ...f, caption } : f)) });
    try { await adminActualizarCaptionFoto(fotoId, caption); } catch { toast('Error al guardar la leyenda.', 'error'); }
  };

  const elegirPortada = async (albumId, fotoId) => {
    setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, portada_foto_id: fotoId } : a)));
    setAbierto((prev) => prev && prev.id === albumId && { ...prev, portada_foto_id: fotoId });
    try { await adminSetPortada(albumId, fotoId); toast('Portada actualizada ✅'); } catch { toast('Error al elegir la portada.', 'error'); }
  };

  const moverFoto = async (albumId, index, direccion) => {
    const album = albums.find((a) => a.id === albumId);
    const otroIndex = index + direccion;
    if (!album || otroIndex < 0 || otroIndex >= album.fotos.length) return;
    const fotoA = album.fotos[index], fotoB = album.fotos[otroIndex];
    const nuevasFotos = [...album.fotos];
    [nuevasFotos[index], nuevasFotos[otroIndex]] = [
      { ...fotoB, orden: fotoA.orden }, { ...fotoA, orden: fotoB.orden },
    ];
    setAlbums((prev) => prev.map((a) => (a.id === albumId ? { ...a, fotos: nuevasFotos } : a)));
    setAbierto((prev) => prev && prev.id === albumId && { ...prev, fotos: nuevasFotos });
    try { await adminIntercambiarOrdenFotos(fotoA, fotoB); } catch { toast('Error al reordenar.', 'error'); }
  };

  const copiarLink = (shareToken) => {
    const url = `${window.location.origin}/nuestros-clientes?album=${shareToken}`;
    navigator.clipboard?.writeText(url);
    toast('Link copiado 📋');
  };

  const diagnostico = (
    <p style={{ color: 'var(--muted)', fontSize: 10.5, marginBottom: 14 }}>
      Conectado como: {sesionEmail || 'cargando...'}
    </p>
  );

  if (loading) return <div>{diagnostico}<p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando álbumes...</p></div>;

  if (errorCarga) {
    return (
      <div>
        {diagnostico}
        <div style={{ background: 'rgba(255,68,85,0.12)', border: '1px solid rgba(255,68,85,0.4)', borderRadius: 12, padding: 16 }}>
          <p style={{ color: '#FF4455', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>No se pudieron cargar los álbumes</p>
          <p style={{ color: '#FF4455', fontSize: 12, marginBottom: 12, wordBreak: 'break-word' }}>{errorCarga}</p>
          <button onClick={cargar} style={{ background: 'rgba(255,68,85,0.2)', border: '1px solid rgba(255,68,85,0.5)', color: '#FF4455', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700 }}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {diagnostico}
      {albums.length === 0 && (
        <p style={{ color: 'var(--gold, #FFB800)', fontSize: 11.5, marginBottom: 14 }}>
          No llegó ningún álbum de la base de datos (sin error) — {sesionEmail}
        </p>
      )}
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>NUEVO ÁLBUM</p>
        <input value={nuevoTitulo} onChange={(e) => setNuevoTitulo(e.target.value)} placeholder="Título interno (ej: Familia Pérez - Boa Vista a Curitiba)" style={{
          width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 11, color: 'var(--white)', fontSize: 13, marginBottom: 8,
        }} />
        <input value={nuevoTituloPublico} onChange={(e) => setNuevoTituloPublico(e.target.value)} placeholder="Título público (opcional, lo ve todo el mundo)" style={{
          width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 11, color: 'var(--white)', fontSize: 13, marginBottom: 12,
        }} />
        <button onClick={crear} disabled={creando} style={{ background: 'var(--orange)', color: 'white', fontWeight: 700, fontSize: 13, borderRadius: 10, padding: '10px 18px' }}>
          {creando ? 'Creando...' : '+ Crear álbum'}
        </button>
      </div>

      <p style={{ fontSize: 10.5, color: 'var(--muted)', marginBottom: 8 }}>Usa ▲▼ para ordenar cómo se ven los álbumes en la página pública.</p>
      {albums.map((a, index) => (
        <div key={a.id} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button onClick={() => moverAlbum(index, -1)} disabled={index === 0} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 8px', fontSize: 12, opacity: index === 0 ? 0.4 : 1 }}>▲</button>
              <button onClick={() => moverAlbum(index, 1)} disabled={index === albums.length - 1} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 8px', fontSize: 12, opacity: index === albums.length - 1 ? 0.4 : 1 }}>▼</button>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--white)' }}>{a.titulo}</p>
              <p style={{ fontSize: 11, color: 'var(--muted)' }}>{a.fotos.length} foto{a.fotos.length !== 1 ? 's' : ''} · {a.estado} · {a.publicado ? 'publicado' : 'oculto'}</p>
            </div>
            <button onClick={() => setAbierto(abierto?.id === a.id ? null : a)} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 8, padding: '6px 12px', fontSize: 12, flexShrink: 0 }}>
              {abierto?.id === a.id ? 'Cerrar' : 'Abrir'}
            </button>
          </div>

          {abierto?.id === a.id && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--navy-border)' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>TÍTULO PÚBLICO (lo ve todo el mundo, en la portada del álbum)</p>
              <input
                defaultValue={a.titulo_publico || ''}
                placeholder="ej: Holguín - Curitiba vía Sky High"
                onBlur={(e) => { if (e.target.value !== (a.titulo_publico || '')) toggleCampo(a.id, 'titulo_publico', e.target.value.trim() || null); }}
                style={{ width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 11, color: 'var(--white)', fontSize: 13, marginBottom: 16 }}
              />
              <p style={{ fontSize: 10.5, color: 'var(--muted)', marginBottom: 8 }}>El orden de arriba a abajo es el orden en que las va a ver la gente — usa ▲▼ para armar la secuencia de la historia.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {a.fotos.map((f, index) => (
                  <div key={f.id} style={{ display: 'flex', gap: 8, background: 'var(--navy)', borderRadius: 10, padding: 8 }}>
                    <div style={{ position: 'relative', width: 60, height: 60, flexShrink: 0 }}>
                      {f.tipo === 'video' ? (
                        <video src={f.url} muted style={{
                          width: 60, height: 60, borderRadius: 8, objectFit: 'cover', display: 'block',
                          border: (a.portada_foto_id === f.id) ? '2px solid var(--gold, #FFB800)' : 'none',
                        }} />
                      ) : (
                        <img src={f.url} alt="" style={{
                          width: 60, height: 60, borderRadius: 8, objectFit: 'cover', display: 'block',
                          border: (a.portada_foto_id ? a.portada_foto_id === f.id : index === 0) ? '2px solid var(--gold, #FFB800)' : 'none',
                        }} />
                      )}
                      {f.tipo === 'video' && (
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'white', textShadow: '0 1px 4px rgba(0,0,0,0.8)', pointerEvents: 'none' }}>▶</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input
                        defaultValue={f.caption || ''}
                        placeholder="Leyenda de esta foto (ej: cuando se emitió el pasaje)"
                        onBlur={(e) => { if (e.target.value !== (f.caption || '')) guardarCaption(a.id, f.id, e.target.value); }}
                        style={{ width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: '7px 9px', color: 'var(--white)', fontSize: 11.5, marginBottom: 6 }}
                      />
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button onClick={() => moverFoto(a.id, index, -1)} disabled={index === 0} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 8px', fontSize: 11, opacity: index === 0 ? 0.4 : 1 }}>▲</button>
                        <button onClick={() => moverFoto(a.id, index, 1)} disabled={index === a.fotos.length - 1} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 8px', fontSize: 11, opacity: index === a.fotos.length - 1 ? 0.4 : 1 }}>▼</button>
                        {f.tipo !== 'video' && (
                          <button
                            onClick={() => elegirPortada(a.id, f.id)}
                            disabled={a.portada_foto_id ? a.portada_foto_id === f.id : index === 0}
                            style={{
                              background: (a.portada_foto_id ? a.portada_foto_id === f.id : index === 0) ? 'rgba(255,184,0,0.2)' : 'var(--navy-card)',
                              border: '1px solid rgba(255,184,0,0.4)', color: 'var(--gold, #FFB800)', borderRadius: 6, padding: '4px 8px', fontSize: 11,
                            }}
                          >★ Portada</button>
                        )}
                        {f.tipo !== 'video' && (
                          <button onClick={() => setEditor({ albumId: a.id, mode: 'editar', fotoId: f.id, path: f.path, url: f.url })} style={{ background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.4)', color: 'var(--orange)', borderRadius: 6, padding: '4px 8px', fontSize: 11 }}>✏️</button>
                        )}
                        <a href={f.url} download target="_blank" rel="noopener noreferrer" style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 8px', fontSize: 11, textDecoration: 'none' }}>⬇</a>
                        <button onClick={() => borrarFoto(a.id, f.id, f.path)} style={{ background: 'rgba(255,68,85,0.12)', border: '1px solid rgba(255,68,85,0.35)', color: '#FF4455', borderRadius: 6, padding: '4px 8px', fontSize: 11 }}>✕</button>
                      </div>
                    </div>
                  </div>
                ))}
                <label style={{ borderRadius: 10, border: '1px dashed var(--navy-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
                  + Agregar foto(s) o video(s)
                  <input type="file" accept="image/*,video/*" multiple hidden onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const fotos = files.filter((f) => f.type.startsWith('image/'));
                    const videos = files.filter((f) => f.type.startsWith('video/'));
                    // Los videos se suben directo, uno tras otro; las fotos
                    // pasan por el editor de pixelado en cola, como ya funcionaba.
                    videos.forEach((v) => subirVideoDirecto(a.id, v));
                    if (fotos.length > 0) {
                      setEditor({ albumId: a.id, mode: 'nueva', file: fotos[0] });
                      setColaPendiente(fotos.slice(1));
                    }
                    e.target.value = '';
                  }} />
                </label>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button onClick={() => toggleCampo(a.id, 'estado', a.estado === 'abierto' ? 'cerrado' : 'abierto')} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 8, padding: '7px 12px', fontSize: 11.5 }}>
                  Marcar como {a.estado === 'abierto' ? 'cerrado' : 'abierto'}
                </button>
                <button onClick={() => toggleCampo(a.id, 'publicado', !a.publicado)} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 8, padding: '7px 12px', fontSize: 11.5 }}>
                  {a.publicado ? 'Ocultar' : 'Publicar'} álbum
                </button>
                <button onClick={() => copiarLink(a.share_token)} style={{ background: 'rgba(255,184,0,0.15)', border: '1px solid rgba(255,184,0,0.4)', color: 'var(--gold, #FFB800)', borderRadius: 8, padding: '7px 12px', fontSize: 11.5 }}>
                  🔗 Copiar link
                </button>
                <button onClick={() => eliminar(a.id)} style={{ background: 'rgba(255,68,85,0.12)', border: '1px solid rgba(255,68,85,0.35)', color: '#FF4455', borderRadius: 8, padding: '7px 12px', fontSize: 11.5 }}>
                  Eliminar álbum
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {editor && (
        <PixeladorFoto
          source={editor.mode === 'nueva' ? { file: editor.file } : { url: editor.url }}
          saving={subiendo}
          onCancel={cancelarEditor}
          onConfirm={editor.mode === 'nueva' ? confirmarFoto : confirmarEdicion}
        />
      )}
      {colaPendiente.length > 0 && (
        <p style={{ position: 'fixed', top: 10, left: 0, right: 0, textAlign: 'center', color: 'var(--white)', fontSize: 12, zIndex: 401 }}>
          Quedan {colaPendiente.length} foto{colaPendiente.length !== 1 ? 's' : ''} más en esta selección
        </p>
      )}
    </div>
  );
}

// ── Invitaciones a dejar reseña (link personal, un solo uso) ────────────
// Complementa la lista de teléfonos verificados: esta es para cuando
// Yoedis ya sabe con certeza quién viajó con él y le manda el link
// directo por WhatsApp, sin pasar por el camino abierto del grupo.
function InvitacionesTab() {
  const [invitaciones, setInvitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [etiqueta, setEtiqueta] = useState('');
  const [creando, setCreando] = useState(false);

  const cargar = () => adminListarInvitacionesResena().then(setInvitaciones).finally(() => setLoading(false));
  useEffect(() => { cargar(); }, []);

  const crear = async () => {
    setCreando(true);
    try {
      const inv = await adminCrearInvitacionResena(etiqueta);
      setInvitaciones((prev) => [inv, ...prev]);
      setEtiqueta('');
      const url = `${window.location.origin}/?tab=nuestros_clientes&resena=${inv.codigo}`;
      navigator.clipboard?.writeText(url);
      toast('Link creado y copiado 📋');
    } catch { toast('Error al crear la invitación.', 'error'); }
    setCreando(false);
  };

  const copiar = (codigo) => {
    const url = `${window.location.origin}/?tab=nuestros_clientes&resena=${codigo}`;
    navigator.clipboard?.writeText(url);
    toast('Link copiado 📋');
  };

  const eliminar = async (id) => {
    try {
      await adminEliminarInvitacionResena(id);
      setInvitaciones((prev) => prev.filter((i) => i.id !== id));
    } catch { toast('Error al eliminar.', 'error'); }
  };

  // Link fijo (no cambia) para postear en el grupo de WhatsApp -- a
  // diferencia de las invitaciones de abajo, es el mismo siempre y
  // cualquiera puede verlo sin loguearse. Yoedis lo pidió acá mismo para
  // no tener que pedírmelo cada vez que quiere volver a compartirlo.
  const linkGrupo = `${window.location.origin}/nuestros-clientes`;
  const copiarLinkGrupo = () => { navigator.clipboard?.writeText(linkGrupo); toast('Link copiado 📋'); };

  return (
    <div>
      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>LINK PARA EL GRUPO DE WHATSAPP</p>
        <p style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
          Este es fijo (siempre el mismo) -- cualquiera lo puede abrir sin loguearse, para ver fotos y reseñas.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: 9, fontSize: 10.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{linkGrupo}</div>
          <button onClick={copiarLinkGrupo} style={{ background: 'var(--orange)', color: 'white', fontSize: 11, fontWeight: 700, borderRadius: 8, padding: '9px 12px', flexShrink: 0 }}>Copiar</button>
        </div>
      </div>

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>NUEVA INVITACIÓN</p>
        <p style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>Genera un link de un solo uso y mándaselo por WhatsApp solo al cliente que de verdad te compró algo.</p>
        <input value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)} placeholder="Nombre del cliente (solo para tu referencia)" style={{
          width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 11, color: 'var(--white)', fontSize: 13, marginBottom: 12,
        }} />
        <button onClick={crear} disabled={creando} style={{ background: 'var(--orange)', color: 'white', fontWeight: 700, fontSize: 13, borderRadius: 10, padding: '10px 18px' }}>
          {creando ? 'Creando...' : '+ Generar link'}
        </button>
      </div>

      {loading ? <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando...</p> : invitaciones.map((i) => (
        <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{i.etiqueta || '(sin nombre)'}</p>
            <p style={{ fontSize: 11, color: i.usado ? 'var(--muted)' : 'var(--success)' }}>{i.usado ? 'Ya usado' : 'Disponible'}</p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {!i.usado && <button onClick={() => copiar(i.codigo)} style={{ background: 'rgba(255,184,0,0.15)', border: '1px solid rgba(255,184,0,0.4)', color: 'var(--gold, #FFB800)', borderRadius: 8, padding: '7px 10px', fontSize: 11 }}>🔗</button>}
            <button onClick={() => eliminar(i.id)} style={{ background: 'rgba(255,68,85,0.12)', border: '1px solid rgba(255,68,85,0.35)', color: '#FF4455', borderRadius: 8, padding: '7px 10px', fontSize: 11 }}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Reseñas ──────────────────────────────────────────────────────────
function ResenasTab() {
  const [filtro, setFiltro] = useState('pendiente');
  const [resenas, setResenas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respuestas, setRespuestas] = useState({});
  const [telefonosVerificados, setTelefonosVerificados] = useState(new Set());

  const cargar = () => {
    setLoading(true);
    Promise.all([adminListarResenas(filtro), adminListarTelefonosVerificados()])
      .then(([r, verificados]) => {
        setResenas(r);
        setTelefonosVerificados(new Set(verificados.map((v) => v.telefono)));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { cargar(); }, [filtro]);

  const moderar = async (id, estado) => {
    try {
      await adminModerarResena(id, estado, respuestas[id]);
      setResenas((prev) => prev.filter((r) => r.id !== id));
      toast(estado === 'aprobada' ? 'Reseña publicada ✅' : 'Reseña rechazada.');
    } catch { toast('Error al moderar.', 'error'); }
  };

  // Responder una reseña YA aprobada (a diferencia de la respuesta al
  // momento de aprobar, esto reusa el mismo estado -- solo actualiza
  // respuesta_publica). Yoedis pega acá el texto ya humanizado que arma
  // en el chat con Claude antes de guardarlo.
  const guardarRespuesta = async (id) => {
    try {
      await adminModerarResena(id, 'aprobada', respuestas[id]);
      setResenas((prev) => prev.map((r) => (r.id === id ? { ...r, respuesta_publica: respuestas[id]?.trim() || null } : r)));
      toast('Respuesta guardada ✅');
    } catch { toast('Error al guardar la respuesta.', 'error'); }
  };

  const ocultar = async (id) => {
    try {
      await adminModerarResena(id, 'rechazada', null);
      setResenas((prev) => prev.filter((r) => r.id !== id));
      toast('Reseña ocultada del público.');
    } catch { toast('Error al ocultar.', 'error'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta reseña para siempre? No se puede deshacer.')) return;
    try {
      await adminEliminarResena(id);
      setResenas((prev) => prev.filter((r) => r.id !== id));
      toast('Reseña eliminada.');
    } catch { toast('Error al eliminar.', 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['pendiente', 'aprobada', 'rechazada'].map((f) => (
          <button key={f} onClick={() => setFiltro(f)} style={{
            padding: '8px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: filtro === f ? 'var(--orange)' : 'var(--navy-card)', color: filtro === f ? 'white' : 'var(--muted)',
          }}>{f}</button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando...</p> : resenas.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>Nada por aquí.</p>
      ) : resenas.map((r) => (
        <div key={r.id} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{r.nombre}</span>
            <Estrellas n={r.rating} />
          </div>
          <p style={{ fontSize: 10.5, color: 'var(--orange)', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
            {CATEGORIAS_RESENA.find((c) => c.id === r.categoria)?.label} {r.telefono ? `· 📞 ${r.telefono}` : ''}
            {r.telefono && (
              telefonosVerificados.has(normalizarTelefono(r.telefono))
                ? <span style={{ color: 'var(--success)', marginLeft: 6 }}>✓ Verificado</span>
                : <span style={{ color: 'var(--muted)', marginLeft: 6, fontWeight: 500, textTransform: 'none' }}>· no está en tu lista, revisa a mano</span>
            )}
          </p>
          <p style={{ fontSize: 13, color: 'var(--white)', lineHeight: 1.5, marginBottom: 10 }}>{r.comentario}</p>

          {r.fotosUrls?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {r.fotosUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />
                </a>
              ))}
            </div>
          )}

          {filtro === 'pendiente' && (
            <>
              <textarea placeholder="Respuesta pública (opcional)" value={respuestas[r.id] || ''} onChange={(e) => setRespuestas((p) => ({ ...p, [r.id]: e.target.value }))} rows={2} style={{
                width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: 8, color: 'var(--white)', fontSize: 12, marginBottom: 10, resize: 'vertical',
              }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => moderar(r.id, 'aprobada')} style={{ flex: 1, background: 'var(--success)', color: 'white', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 0' }}>Aprobar</button>
                <button onClick={() => moderar(r.id, 'rechazada')} style={{ flex: 1, background: 'rgba(255,68,85,0.15)', border: '1px solid rgba(255,68,85,0.4)', color: '#FF4455', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 0' }}>Rechazar</button>
              </div>
            </>
          )}
          {filtro === 'aprobada' && (
            <>
              <textarea
                placeholder="Responder públicamente (opcional)"
                value={respuestas[r.id] ?? r.respuesta_publica ?? ''}
                onChange={(e) => setRespuestas((p) => ({ ...p, [r.id]: e.target.value }))}
                rows={2}
                style={{ width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: 8, color: 'var(--white)', fontSize: 12, marginBottom: 10, resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => guardarRespuesta(r.id)} style={{ flex: 1, background: 'rgba(255,184,0,0.15)', border: '1px solid rgba(255,184,0,0.4)', color: 'var(--gold, #FFB800)', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 0' }}>Guardar respuesta</button>
                <button onClick={() => ocultar(r.id)} style={{ flex: 1, background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 0' }}>Ocultar del público</button>
                <button onClick={() => eliminar(r.id)} style={{ background: 'rgba(255,68,85,0.15)', border: '1px solid rgba(255,68,85,0.4)', color: '#FF4455', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 14px' }}>🗑</button>
              </div>
            </>
          )}
          {filtro === 'rechazada' && (
            <button onClick={() => eliminar(r.id)} style={{ width: '100%', background: 'rgba(255,68,85,0.15)', border: '1px solid rgba(255,68,85,0.4)', color: '#FF4455', fontWeight: 700, fontSize: 12.5, borderRadius: 8, padding: '9px 0' }}>🗑 Eliminar para siempre</button>
          )}
          {filtro !== 'aprobada' && r.respuesta_publica && (
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--navy-border)' }}>Respuesta: {r.respuesta_publica}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Teléfonos verificados (agiliza la moderación de reseñas) ────────────
function TelefonosVerificadosCard() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [verLista, setVerLista] = useState(false);

  const cargar = () => adminListarTelefonosVerificados().then(setLista).finally(() => setLoading(false));
  useEffect(() => { cargar(); }, []);

  const agregar = async () => {
    if (!texto.trim()) return;
    setGuardando(true);
    try {
      const n = await adminAgregarTelefonosVerificados(texto);
      setTexto('');
      await cargar();
      toast(n > 0 ? `${n} teléfono${n !== 1 ? 's' : ''} agregado${n !== 1 ? 's' : ''} ✅` : 'No encontré ningún teléfono válido en ese texto.');
    } catch { toast('Error al guardar la lista.', 'error'); }
    setGuardando(false);
  };

  const eliminar = async (telefono) => {
    try { await adminEliminarTelefonoVerificado(telefono); setLista((prev) => prev.filter((t) => t.telefono !== telefono)); }
    catch { toast('Error al eliminar.', 'error'); }
  };

  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
      <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>TELÉFONOS VERIFICADOS ({loading ? '...' : lista.length})</p>
      <p style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
        Pega aquí los teléfonos de clientes reales de vuelos (uno por línea, copiados de WhatsApp). No bloquea a nadie más -- solo marca "✓ Verificado" en el panel de Reseñas para que moderes más rápido.
      </p>
      <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={4} placeholder={'Juan Pérez +55 41 99999-9999\n41988887777'} style={{
        width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 11, color: 'var(--white)', fontSize: 13, marginBottom: 10, resize: 'vertical',
      }} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={agregar} disabled={guardando} style={{ background: 'var(--orange)', color: 'white', fontWeight: 700, fontSize: 12.5, borderRadius: 10, padding: '9px 16px' }}>
          {guardando ? 'Guardando...' : '+ Agregar a la lista'}
        </button>
        {lista.length > 0 && (
          <button onClick={() => setVerLista((v) => !v)} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 10, padding: '9px 16px', fontSize: 12.5 }}>
            {verLista ? 'Ocultar lista' : 'Ver lista completa'}
          </button>
        )}
      </div>
      {verLista && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
          {lista.map((t) => (
            <div key={t.telefono} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--navy)', borderRadius: 8, padding: '7px 10px' }}>
              <span style={{ fontSize: 12, color: 'var(--white)' }}>{t.telefono} {t.etiqueta ? `· ${t.etiqueta}` : ''}</span>
              <button onClick={() => eliminar(t.telefono)} style={{ background: 'rgba(255,68,85,0.12)', border: '1px solid rgba(255,68,85,0.35)', color: '#FF4455', borderRadius: 6, padding: '3px 8px', fontSize: 11 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Estadísticas: cuánta gente entra a ver álbumes/reseñas, por día,
// semana y mes -- agregado en el cliente a partir de clientes_eventos,
// mismo criterio que VuelosEstadisticas.jsx.
const NC_TIMEZONE = 'America/Sao_Paulo';

const ncFechaLocalStr = (createdAt) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: NC_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(createdAt));

const ncFechaLocalCorta = (fechaLocalStr) =>
  new Date(fechaLocalStr + 'T00:00:00').toLocaleDateString('es', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

// Lunes de la semana de esa fecha (fechaStr en formato YYYY-MM-DD).
const ncLunesDeSemana = (fechaStr) => {
  const d = new Date(fechaStr + 'T00:00:00Z');
  const diaSemana = d.getUTCDay(); // 0 = domingo
  const offset = diaSemana === 0 ? 6 : diaSemana - 1;
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
};

const ncFormatSemanaLabel = (lunesStr) => {
  const inicio = new Date(lunesStr + 'T00:00:00Z');
  const fin = new Date(inicio);
  fin.setUTCDate(fin.getUTCDate() + 6);
  const fmt = (d) => d.toLocaleDateString('es', { timeZone: 'UTC', day: 'numeric', month: 'short' });
  return `${fmt(inicio)} – ${fmt(fin)}`;
};

const ncMesLocalStr = (fechaStr) => fechaStr.slice(0, 7); // YYYY-MM

const ncFormatMesLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const PERIODOS_STATS = [
  { dias: 30, label: '30 días' },
  { dias: 90, label: '90 días' },
  { dias: null, label: 'Todo' },
];

function ExclusionPropiaClientes() {
  const [activo, setActivo] = useState(isClientesTrackingOptedOut());
  const toggle = () => {
    const nuevo = !activo;
    setClientesTrackingOptOut(nuevo);
    setActivo(nuevo);
  };
  return (
    <div style={{
      background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12,
      padding: 14, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>No contar mis visitas en este navegador</p>
        <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
          Este botón solo aplica al navegador donde lo activas (este panel). Para el link público, ábrelo una vez con{' '}
          <code>?notrack=1</code> al final (ej. .../nuestros-clientes?notrack=1) en cada teléfono/navegador que uses para probarlo -- después de eso ya no hace falta repetirlo ahí.
        </p>
      </div>
      <button onClick={toggle} style={{
        flexShrink: 0, fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '6px 12px', whiteSpace: 'nowrap',
        background: activo ? 'rgba(0,196,140,0.15)' : 'var(--navy)',
        color: activo ? 'var(--success)' : 'var(--muted)',
        border: `1px solid ${activo ? 'rgba(0,196,140,0.4)' : 'var(--navy-border)'}`,
      }}>{activo ? '✅ Activado' : 'Desactivado'}</button>
    </div>
  );
}

function TablaConteo({ titulo, filas, vacio }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>{titulo}</p>
      {filas.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--muted)' }}>{vacio}</p>
      ) : filas.map((f) => (
        <div key={f.key} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
          borderRadius: 10, padding: '10px 12px', marginBottom: 6,
        }}>
          <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--white)', textTransform: 'capitalize' }}>{f.label}</p>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--white)' }}>{f.visitas}</p>
            <p style={{ fontSize: 10, color: 'var(--muted)' }}>{f.unicos} única{f.unicos !== 1 ? 's' : ''}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function EstadisticasTab() {
  const [dias, setDias] = useState(30);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminGetClientesEventos(dias).then((data) => { setEventos(data); setLoading(false); });
  }, [dias]);

  if (loading) return (
    <>
      <ExclusionPropiaClientes />
      <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando estadísticas...</p>
    </>
  );

  const totalVisitas = eventos.length;
  const totalUnicos = new Set(eventos.map((e) => e.session_id).filter(Boolean)).size;
  const publico = eventos.filter((e) => e.origen === 'publico').length;
  const autenticado = eventos.filter((e) => e.origen === 'autenticado').length;

  const agrupar = (keyFn, labelFn, limite) => {
    const mapa = {};
    eventos.forEach((e) => {
      const fechaStr = ncFechaLocalStr(e.created_at);
      const key = keyFn(fechaStr);
      if (!mapa[key]) mapa[key] = { visitas: 0, sesiones: new Set() };
      mapa[key].visitas += 1;
      if (e.session_id) mapa[key].sesiones.add(e.session_id);
    });
    return Object.entries(mapa)
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, limite)
      .map(([key, v]) => ({ key, label: labelFn(key), visitas: v.visitas, unicos: v.sesiones.size }));
  };

  const porDia = agrupar((f) => f, ncFechaLocalCorta, 30);
  const porSemana = agrupar(ncLunesDeSemana, ncFormatSemanaLabel, 12);
  const porMes = agrupar(ncMesLocalStr, ncFormatMesLabel, 12);

  return (
    <div>
      <ExclusionPropiaClientes />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {PERIODOS_STATS.map((p) => (
          <button key={p.dias ?? 'todo'} onClick={() => setDias(p.dias)} style={{
            flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: dias === p.dias ? 'var(--orange)' : 'var(--navy-card)',
            color: dias === p.dias ? 'white' : 'var(--muted)',
            border: '1px solid var(--navy-border)',
          }}>{p.label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 24 }}>
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
          <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{totalVisitas}</p>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>VISITAS</p>
        </div>
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
          <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{totalUnicos}</p>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>PERSONAS ÚNICAS APROX.</p>
        </div>
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
          <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)' }}>{publico}</p>
          <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>SIN LOGIN</p>
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
        {publico} desde el link público (WhatsApp) y {autenticado} desde dentro de la app. "Únicas aprox." se calcula por dispositivo/navegador, no por persona real -- si alguien entra desde el celular y luego desde la computadora, cuenta dos veces.
      </p>

      <TablaConteo titulo="POR DÍA (últimos 30)" filas={porDia} vacio="Todavía no hay visitas registradas." />
      <TablaConteo titulo="POR SEMANA (últimas 12)" filas={porSemana} vacio="Todavía no hay visitas registradas." />
      <TablaConteo titulo="POR MES (últimos 12)" filas={porMes} vacio="Todavía no hay visitas registradas." />
    </div>
  );
}

// ── Ajustes / publicación ────────────────────────────────────────────
function AjustesTab() {
  const [config, setConfig] = useState(null);
  const [googleLink, setGoogleLink] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { adminGetConfig().then((c) => { setConfig(c); setGoogleLink(c.google_review_link || ''); }); }, []);

  const togglePublicado = async () => {
    const nuevo = !config.publicado;
    setConfig((c) => ({ ...c, publicado: nuevo }));
    try { await adminActualizarConfig({ publicado: nuevo }); toast(nuevo ? '¡Publicado para todo el mundo! 🎉' : 'Vuelto a modo privado.'); }
    catch { toast('Error al actualizar.', 'error'); }
  };

  const guardarGoogleLink = async () => {
    setGuardando(true);
    try { await adminActualizarConfig({ google_review_link: googleLink.trim() || null }); toast('Link de Google guardado ✅'); }
    catch { toast('Error al guardar.', 'error'); }
    setGuardando(false);
  };

  const copiar = (url) => { navigator.clipboard?.writeText(url); toast('Link copiado 📋'); };

  if (!config) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Cargando...</p>;

  const previewGuyana = `${window.location.origin}/vuelos-guyana?ncpreview=${config.preview_token}`;
  const previewDirecto = `${window.location.origin}/nuestros-clientes?preview=${config.preview_token}`;

  return (
    <div>
      <div style={{ background: config.publicado ? 'rgba(0,196,140,0.1)' : 'var(--navy-card)', border: `1px solid ${config.publicado ? 'rgba(0,196,140,0.4)' : 'var(--navy-border)'}`, borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <p style={{ fontSize: 13.5, fontWeight: 800, color: config.publicado ? 'var(--success)' : 'var(--white)', marginBottom: 4 }}>
          {config.publicado ? '✅ Publicado — todo el mundo lo ve' : '🔒 Privado — solo tú lo ves con el link de vista previa'}
        </p>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
          Mientras esté en privado, nadie puede ver álbumes ni reseñas ni mandar una reseña nueva -- ni en la app ni en el link público de vuelos.
        </p>
        <button onClick={togglePublicado} style={{
          background: config.publicado ? 'rgba(255,68,85,0.15)' : 'var(--orange)', color: config.publicado ? '#FF4455' : 'white',
          border: config.publicado ? '1px solid rgba(255,68,85,0.4)' : 'none', fontWeight: 800, fontSize: 13, borderRadius: 10, padding: '11px 18px',
        }}>{config.publicado ? 'Volver a privado' : 'Publicar para todo el mundo'}</button>
      </div>

      {!config.publicado && (
        <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>TUS LINKS DE VISTA PREVIA</p>
          <p style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>Ábrelos una vez desde tu teléfono y te quedan guardados en ese dispositivo:</p>
          {[{ label: 'Dentro de /vuelos-guyana', url: previewGuyana }, { label: 'Página directa', url: previewDirecto }].map((l) => (
            <div key={l.url} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ flex: 1, background: 'var(--navy)', borderRadius: 8, padding: 9, fontSize: 10.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url}</div>
              <button onClick={() => copiar(l.url)} style={{ background: 'var(--orange)', color: 'white', fontSize: 11, fontWeight: 700, borderRadius: 8, padding: '9px 12px', flexShrink: 0 }}>Copiar</button>
            </div>
          ))}
        </div>
      )}

      <TelefonosVerificadosCard />

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>LINK DE RESEÑAS DE GOOGLE</p>
        <p style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>Se le muestra al cliente después de una reseña de 4-5 estrellas, para invitarlo a repetirla en Google.</p>
        <input value={googleLink} onChange={(e) => setGoogleLink(e.target.value)} placeholder="https://g.page/r/..." style={{
          width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: 11, color: 'var(--white)', fontSize: 13, marginBottom: 10,
        }} />
        <button onClick={guardarGoogleLink} disabled={guardando} style={{ background: 'var(--orange)', color: 'white', fontWeight: 700, fontSize: 12.5, borderRadius: 10, padding: '9px 16px' }}>
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

export default function NuestrosClientesAdminPanel({ initialTab }) {
  const [tab, setTab] = useState(initialTab || 'albumes');
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {[{ id: 'albumes', label: '📷 Álbumes' }, { id: 'invitaciones', label: '✉️ Invitar' }, { id: 'resenas', label: '⭐ Reseñas' }, { id: 'estadisticas', label: '📊 Estadísticas' }, { id: 'ajustes', label: '⚙️ Ajustes' }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: '1 1 45%', padding: '9px 0', borderRadius: 8, fontSize: 11.5, fontWeight: 700,
            background: tab === t.id ? 'var(--orange)' : 'var(--navy-card)', color: tab === t.id ? 'white' : 'var(--muted)',
          }}>{t.label}</button>
        ))}
      </div>
      {tab === 'albumes' && <AlbumesTab />}
      {tab === 'invitaciones' && <InvitacionesTab />}
      {tab === 'resenas' && <ResenasTab />}
      {tab === 'estadisticas' && <EstadisticasTab />}
      {tab === 'ajustes' && <AjustesTab />}
    </div>
  );
}
